import {routes} from '@utilities/constants/routes.tsx'
import {
    CalendarDateIcon,
    QueueListIcon,
    FolderIcon,
    Home03Icon,
    IconProps,
    Settings01Icon,
    Users01Icon,
    FileCheck02Icon,
    PieChart03Icon,
    UserSquareIcon,
    AlertTriangleIcon
} from '@components/ui/icon/Icon.tsx'
import {StyledNavbar, StyledNavLink} from '@components/commons/navbar/style.ts'
import Logo from '@assets/images/logo.svg'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Divider} from '@components/ui/divider/Divider.tsx'
import {Tooltip} from '@components/ui/tooltip/Tooltip.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {useTranslation} from '@/translations/i18n.tsx'
import {ForwardRefExoticComponent, RefAttributes} from 'react'
import {usePermissions} from '@/features/auth/store/store'
import {useBugReporterStore} from '@/features/bug-reporter'

export const Navbar = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const openBugReporter = useBugReporterStore(state => state.open)

    const navLinks = [
        ...(permissions?.settings?.read
            ? [
                  {
                      label: t('commons:dashboard'),
                      path: routes.DASHBOARD.handle.path,
                      Icon: PieChart03Icon
                  }
              ]
            : [
                  {
                      label: t('commons:home'),
                      path: routes.HOME.handle.path,
                      Icon: Home03Icon
                  }
              ]),
        ...(permissions?.agenda.read
            ? [
                  {
                      label: t('agenda:title'),
                      path: routes.AGENDA_ROUTE.handle.path,
                      Icon: CalendarDateIcon
                  }
              ]
            : []),
        ...(permissions?.patients?.read
            ? [{label: t('commons:patients'), path: routes.PATIENTS_ROUTE.handle.path, Icon: Users01Icon}]
            : []),
        ...(permissions?.requests?.read
            ? [
                  {
                      label: t('treatment_requests:title'),
                      path: routes.TREATMENT_REQUESTS_ROUTE.handle.path,
                      Icon: QueueListIcon
                  }
              ]
            : []),
        ...(permissions?.projects?.read
            ? [
                  {
                      label: t('commons:projects', {count: Infinity}),
                      path: routes.PROJECTS_ROUTE.handle.path,
                      Icon: FolderIcon
                  }
              ]
            : []),
        ...(permissions?.accesses?.read
            ? [
                  {
                      label: t('accesses:title'),
                      path: routes.ACCESSES_ROUTE.handle.path,
                      Icon: FileCheck02Icon
                  }
              ]
            : [])
    ] as const satisfies Array<{
        label: string
        Icon: ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>
        path: string
    }>

    return (
        <StyledNavbar>
            <Flexbox direction="column" gap={3} align="center">
                <img src={Logo} alt={'logo'} width={24} />
                <Divider direction="horizontal" />
                <Flexbox direction="column" gap={2}>
                    {navLinks.map(navLink => (
                        <Tooltip
                            key={navLink.path}
                            isOpenOnClickEnabled={false}
                            contentProps={{side: 'right'}}
                            delayDuration={0}
                            variant="light"
                            content={navLink.label}
                            triggerProps={{asChild: true}}
                        >
                            <StyledNavLink variant="ghost" shape="square" to={navLink.path}>
                                <navLink.Icon />
                            </StyledNavLink>
                        </Tooltip>
                    ))}
                </Flexbox>
            </Flexbox>
            <Flexbox direction="column" gap={2}>
                <Tooltip
                    isOpenOnClickEnabled={false}
                    contentProps={{side: 'right'}}
                    delayDuration={0}
                    variant="light"
                    content="Segnala un problema"
                    triggerProps={{asChild: true}}
                >
                    <Button variant="ghost" size="sm" shape="square" onClick={openBugReporter}>
                        <AlertTriangleIcon size={16} />
                    </Button>
                </Tooltip>
                {permissions?.users?.read && (
                    <Tooltip
                        isOpenOnClickEnabled={false}
                        contentProps={{side: 'right'}}
                        delayDuration={0}
                        variant="light"
                        content={t('commons:users')}
                        triggerProps={{asChild: true}}
                    >
                        <StyledNavLink to={routes.USERS_ROUTE.path} variant="ghost" size="sm" shape="square">
                            <UserSquareIcon />
                        </StyledNavLink>
                    </Tooltip>
                )}
                {permissions?.settings?.read && (
                    <Tooltip
                        isOpenOnClickEnabled={false}
                        contentProps={{side: 'right'}}
                        delayDuration={0}
                        variant="light"
                        content={t('commons:settings')}
                        triggerProps={{asChild: true}}
                    >
                        <StyledNavLink to={routes.USER_GROUPS_ROUTE.path} variant="ghost" size="sm" shape="square">
                            <Settings01Icon />
                        </StyledNavLink>
                    </Tooltip>
                )}
            </Flexbox>
        </StyledNavbar>
    )
}
